import { useDispatch, useSelector } from "react-redux";
import Interaction, {
  InteractionType,
} from "../models/interactions/Interaction";
import { useAddInteraction } from "../features/training_timeline/AddInteractionHook";
import { addInteractionToSelectedTake } from "../app/slices/TrainingSlice";
import usePropHelper from "./PropHelper";
import useValidation from "../features/validation/Validation.hooks";
import {
  addMostRecentInteraction,
  selectSelectedInteractionDefinition,
  selectSelectedInteractionPropStateId,
} from "../app/slices/InteractionPickerSlice";
import { addInteractionWarning } from "../app/slices/ValidationSlice";
import { InteractionDefintion } from "../features/interaction_picker/InteractionDefinitions";

export default function useInteractionPicker() {
  const { createInteraction, createPropInteraction } = useAddInteraction();
  const dispatch = useDispatch();
  const interactionDef = useSelector(selectSelectedInteractionDefinition);
  const selectedPropId = useSelector(selectSelectedInteractionPropStateId);

  const { getUsedPropByInstanceId } = usePropHelper();
  const { getInteractionWarnings } = useValidation();

  const createNewInteraction = (
    interactionDefinition: InteractionDefintion
  ) => {
    if (interactionDefinition.type === InteractionType.DynamicInteraction) {
      // get prop that is selected in the interaction picker
      const propDto = getUsedPropByInstanceId(selectedPropId);
      if (
        !propDto ||
        !propDto.metadata ||
        !propDto.metadata.dynamicInteractions
      )
        return;

      // get the matching dynamic interaction
      const dynInteraction = propDto.metadata.dynamicInteractions.find(
        (interaction) => interaction.title === interactionDefinition.name
      );
      if (!dynInteraction) return;

      // create the dynamic interaction
      const newInteraction = createPropInteraction(
        dynInteraction,
        selectedPropId
      );

      const warnings = getInteractionWarnings(newInteraction);

      return { interaction: newInteraction, warnings: warnings };
    } else {
      // 1. create new interaction
      const interaction = createInteraction(interactionDefinition.type);
      const warnings = getInteractionWarnings(interaction);

      return { interaction: interaction, warnings: warnings };
    }
  };

  /**
   * Add a definition the caller names, rather than whatever the slice happens
   * to hold. The picker highlights with the keyboard and mirrors that to the
   * slice, so naming the definition removes any dependence on that mirror
   * having settled before Enter lands.
   */
  const addNewInteractionFor = (
    interactionDefinition: InteractionDefintion,
    target?: (interaction: Interaction) => void
  ) => {
    const { interaction, warnings } = {
      ...createNewInteraction(interactionDefinition),
    };
    if (!interaction || !warnings) return;

    if (target) {
      // custom add target (e.g. quiz feedback slot)
      target(interaction);
    } else {
      dispatch(addInteractionToSelectedTake(interaction));
      if (interactionDefinition.type !== InteractionType.DynamicInteraction) {
        dispatch(addMostRecentInteraction(interactionDefinition));
      }
    }

    dispatch(
      addInteractionWarning({ interactionId: interaction.id, warnings })
    );
  };

  // create the selected interaction and hand it to a custom target
  // (e.g. a quiz feedback slot) instead of the selected take
  const addNewInteractionWith = (
    target: (interaction: Interaction) => void
  ) => {
    const { interaction, warnings } = {
      ...createNewInteraction(interactionDef),
    };
    if (!interaction || !warnings) return;

    target(interaction);

    dispatch(
      addInteractionWarning({ interactionId: interaction.id, warnings })
    );
  };

  const addNewInteraction = () => {
    if (interactionDef.type === InteractionType.DynamicInteraction) {
      // get prop that is selected in the interaction picker
      const { interaction, warnings } = {
        ...createNewInteraction(interactionDef),
      };
      if (!interaction || !warnings) return;
      // add the interaction to the selected take
      dispatch(addInteractionToSelectedTake(interaction));

      // 4. add warnings to validation slice
      dispatch(
        addInteractionWarning({ interactionId: interaction.id, warnings })
      );
    } else {
      // 1. create new interaction
      //const interaction = createInteraction(interactionDef.type);
      const { interaction, warnings } = {
        ...createNewInteraction(interactionDef),
      };
      if (!interaction || !warnings) return;
      // 2. add interaction to the selected take
      dispatch(addInteractionToSelectedTake(interaction));
      // 3. add the interaction to most recent
      dispatch(addMostRecentInteraction(interactionDef));
      // 4. add warnings to validation slice
      dispatch(
        addInteractionWarning({ interactionId: interaction.id, warnings })
      );
    }
  };

  return {
    createNewInteraction,
    addNewInteraction,
    addNewInteractionWith,
    addNewInteractionFor,
  };
}
